const KST_TIME_ZONE = 'Asia/Seoul';
const KST_OFFSET = '+09:00';

const kstDateTimeFormatter = new Intl.DateTimeFormat('ko-KR', {
  timeZone: KST_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/**
 * Parse API datetime strings (RFC3339, MySQL "YYYY-MM-DD HH:mm:ss", datetime-local).
 * @param {string|number|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseApiDateTime(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  let candidate = trimmed;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(trimmed)) {
    candidate = `${trimmed.replace(' ', 'T')}Z`;
  } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed) && !/[zZ]|[+-]\d{2}:\d{2}$/.test(trimmed)) {
    candidate = `${trimmed}Z`;
  }

  const d = new Date(candidate);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Parse datetime-local string (YYYY-MM-DDTHH:mm) as KST wall clock → UTC ISO string.
 * @param {string|null|undefined} localValue
 * @returns {string|null}
 */
export function parseDateTimeLocalAsKST(localValue) {
  if (localValue == null || localValue === '') return null;
  const normalized = localValue.length === 16 ? `${localValue}:00` : localValue;
  const d = new Date(`${normalized}${KST_OFFSET}`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * Format ISO/RFC3339 value as KST datetime string with suffix.
 * @param {string|null|undefined} isoValue
 * @returns {string}
 */
export function formatDateTimeKST(isoValue) {
  if (isoValue == null || isoValue === '') return '-';
  const d = parseApiDateTime(isoValue);
  if (!d) return String(isoValue);
  return `${kstDateTimeFormatter.format(d)} (KST)`;
}

/**
 * Current KST time as datetime-local string (YYYY-MM-DDTHH:mm).
 * @returns {string}
 */
export function nowDateTimeLocalKST() {
  return dateToDateTimeLocalKST(new Date());
}

/**
 * Convert a Date to datetime-local string in KST wall clock.
 * @param {Date} date
 * @returns {string}
 */
export function dateToDateTimeLocalKST(date) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: KST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type) => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/**
 * Whether datetime-local KST value is strictly in the future.
 * @param {string|null|undefined} localValue
 * @returns {boolean}
 */
export function isFutureKST(localValue) {
  const iso = parseDateTimeLocalAsKST(localValue);
  if (!iso) return false;
  return new Date(iso) > new Date();
}
